/**
 * The form library vocabulary — typed to `GET /api/v1/forms/templates`.
 *
 * Single source of truth for the frontend, in the same spirit as `roles.ts` and
 * `incident-types.ts`: import from here rather than re-declaring a label map
 * (or deriving one from a boolean) in individual screens.
 *
 * CASING IS PER-ENDPOINT, NOT GLOBAL
 * ----------------------------------
 * There is no repo-wide read casing for the forms feature, and assuming one is
 * how this codebase keeps producing blank fields. Verified against the live
 * endpoint (Smith Casing, 2026-08-08) and against
 * `form-library.service.ts::ResolvedTemplate`:
 *
 *   GET /forms/templates    camelCase — isEnabled, isDefault, sortOrder, fieldCount
 *   GET /forms/extractions  snake_case — the handler returns the raw DB row
 *   GET /safety/templates   snake_case — is_system, cloned_from, field_count
 *   PUT /forms/library      snake_case on the WRITE body
 *
 * `FormLibraryTemplate` describes the FIRST of those and nothing else. The
 * safety-templates shape is a different type with different keys; do not read
 * one through the other.
 */

/**
 * Where a template came from. Derived SERVER-SIDE (a CASE over is_system /
 * from_extraction / cloned_from) and returned already resolved.
 *
 * Do NOT recompute this from `is_system` + `cloned_from` on the client. Those
 * fields are not in this payload at all, and a second expression of a figure
 * the API already answers is two answers to one question.
 */
export type TemplateSource = 'platform' | 'replicated' | 'forked' | 'custom';

/**
 * Labels keyed by TemplateSource.
 *
 * `Record<TemplateSource, string>` (not Partial) on purpose: a new source value
 * must fail the build here rather than silently rendering a raw `replicated` —
 * or, worse, falling through a default branch that echoes the enum value and
 * gets reported as a data bug.
 */
export const TEMPLATE_SOURCE_LABELS: Record<TemplateSource, string> = {
  platform: 'Platform',
  replicated: 'Replicated',
  forked: 'Forked',
  custom: 'Custom',
};

/** What each badge actually means, for the row's title attribute. */
export const TEMPLATE_SOURCE_HINTS: Record<TemplateSource, string> = {
  platform: 'A FieldIQ standard form. You can enable, disable and default it — you cannot edit its fields.',
  replicated: 'Built by the AI Form Replicator from a document your company uploaded.',
  forked: 'A copy of another template, editable by your company.',
  custom: 'Built from scratch by your company.',
};

export const TEMPLATE_SOURCE_STYLES: Record<TemplateSource, string> = {
  platform: 'bg-indigo-500/20 text-indigo-300',
  replicated: 'bg-fieldiq-accent/20 text-fieldiq-accent',
  forked: 'bg-sky-500/20 text-sky-300',
  custom: 'bg-emerald-500/20 text-emerald-300',
};

/**
 * A row from `GET /forms/templates`.
 *
 * Typed to the OBSERVED payload, not to a brief. Every key below was confirmed
 * present on a live response before this interface was written — the recurring
 * bug in this repo is an interface that declares a field the API never sends,
 * because TypeScript then certifies the misread.
 */
export interface FormLibraryTemplate {
  id: string;
  name: string;
  description: string | null;
  category: string;
  version: number;
  /** 'active' | 'draft'. 'archived' is filtered out server-side. */
  status: string;
  /** Whether the field app's Forms tab picks this up. NOT library visibility. */
  active: boolean;
  isEnabled: boolean;
  isDefault: boolean;
  source: TemplateSource;
  sortOrder: number;
  fieldCount: number;
  /** Postgres timestamptz rendering, e.g. "2026-04-17 03:26:39.160475+00". */
  createdAt: string;
  /**
   * Present ONLY on an `include_drafts=1` read — the strict response omits the
   * key entirely rather than sending `true`. Read `status === 'draft'` when you
   * need the fact unconditionally; this is the explicit flag the management UI
   * renders its Unpublished badge from.
   */
  isPublished?: boolean;
}

/**
 * Category labels.
 *
 * UNLIKE `roles.ts` and `incident-types.ts`, this is deliberately NOT a closed
 * enum with an exhaustive Record. `category` is a free-text column and the API
 * discovers the list with `SELECT DISTINCT t.category` — a tenant's replicated
 * form can introduce a value that does not exist here, and the six below are
 * simply the ones live today (verified 2026-08-08).
 *
 * That makes a fallback unavoidable. It is a formatter, not a guess at another
 * category's label: `hazard_id` -> "Hazard Id" is honest about an unmapped
 * value in a way that silently bucketing it into "Other" would not be. The
 * hardcoded pill list this replaced did exactly that — it offered "Inspection"
 * and "Other", which matched nothing, while `general` (10 of 16 templates) and
 * `incident` had no pill at all.
 */
export const FORM_CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  jsa: 'Job Safety Analysis',
  near_miss: 'Near Miss',
  toolbox_talk: 'Toolbox Talk',
  hazard_id: 'Hazard Identification',
  incident: 'Incident',
  inspection: 'Inspection',
};

/**
 * `form_templates.category` of a customer approval stamp (api#709). A stamp is
 * picked on the CUSTOMER record and signed on the public review page; it is not
 * a form anyone opens from the library, so it gets no category of its own here.
 */
export const APPROVAL_STAMP_CATEGORY = 'approval_stamp';

export function isApprovalStampTemplate(template: { category?: string | null }): boolean {
  return template.category === APPROVAL_STAMP_CATEGORY;
}

/**
 * The library as the library and builder screens show it — approval stamps
 * removed. The API excludes them from these listings too; this is the same
 * rule held on this side, so a stamp can never surface as its own category
 * group or be offered as a category for a new form.
 */
export function withoutApprovalStamps<T extends { category?: string | null }>(templates: T[]): T[] {
  return templates.filter((t) => !isApprovalStampTemplate(t));
}

export function getFormCategoryLabel(category: string): string {
  if (!category) return 'Uncategorised';
  const mapped = FORM_CATEGORY_LABELS[category];
  if (mapped) return mapped;
  return category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface FormCategoryGroup {
  category: string;
  label: string;
  templates: FormLibraryTemplate[];
}

/**
 * Group a flat library response by category, preserving the server's ordering.
 *
 * The API already returns rows category-by-category with each category's own
 * sort applied, so this must not re-sort: `isDefault` and `sortOrder` were
 * resolved WITHIN a category server-side, and reordering here would present a
 * different answer than the one the API gave.
 */
export function groupByCategory(templates: FormLibraryTemplate[]): FormCategoryGroup[] {
  const groups: FormCategoryGroup[] = [];
  const byCategory = new Map<string, FormCategoryGroup>();

  for (const template of templates) {
    const category = template.category ?? '';
    let group = byCategory.get(category);
    if (!group) {
      group = { category, label: getFormCategoryLabel(category), templates: [] };
      byCategory.set(category, group);
      groups.push(group);
    }
    group.templates.push(template);
  }

  return groups;
}

/**
 * The categories this tenant actually has, in the order the API returned them.
 *
 * The API discovers these with `SELECT DISTINCT t.category` over the same rows
 * it resolves the library from, so a category can never be listed here and
 * resolve empty.
 */
export function listFormCategories(templates: FormLibraryTemplate[]): string[] {
  const seen = new Set<string>();
  const categories: string[] = [];
  // Stamps out here rather than at each caller: the builder and the extraction
  // review screen share one cached category list, so a filter in only one of
  // them would depend on which screen filled the cache first.
  for (const t of withoutApprovalStamps(templates)) {
    const category = t.category;
    if (category && !seen.has(category)) {
      seen.add(category);
      categories.push(category);
    }
  }
  return categories;
}

/**
 * Options for a category `<select>`, given the live categories and the value
 * the select is currently holding.
 *
 * `current` is unioned in, ALWAYS, and that is the whole point of this
 * function. A controlled `<select>` whose `value` matches no `<option>` does
 * not render empty — it renders the FIRST option, while state quietly keeps
 * the real value. The builder shipped exactly that: a template stored as
 * `general` displayed as "Jsa", because `general` was missing from a hardcoded
 * list. The value was never wrong; the screen was, and the next person to
 * touch the select made it true.
 *
 * So: never offer a select a value it cannot show back.
 */
export function buildCategoryOptions(
  liveCategories: string[],
  current: string,
): Array<{ value: string; label: string }> {
  const values = [...liveCategories];
  if (current && !values.includes(current)) values.push(current);
  return values.map((value) => ({ value, label: getFormCategoryLabel(value) }));
}

/** A draft is a template that exists but is not in front of anyone yet. */
export function isDraft(template: FormLibraryTemplate): boolean {
  return template.status === 'draft';
}

/**
 * Platform templates are read-only in CONTENT. They are not read-only in the
 * library: enabling, disabling and defaulting them is the entire point of the
 * page. The badge means "you cannot edit the fields", never "you cannot use it".
 */
export function isContentEditable(template: FormLibraryTemplate): boolean {
  return template.source !== 'platform';
}

/**
 * A template with no fields cannot be filled out.
 *
 * `DynamicSafetyFormScreen` renders whatever `fields` the template carries, so
 * zero fields is a form that opens to a title and a Submit button and writes an
 * empty submission — indistinguishable, on the records list afterwards, from a
 * crew that filled one in. The seed data ships several of these (the same set
 * `UNIMPLEMENTED_FORM_ROUTES` in `useFieldForms.ts` blocks on the field Forms
 * tab); the library page had no equivalent guard, so they presented as ordinary
 * enabled Platform rows a manager could hand to a crew.
 *
 * ABSENT IS NOT ZERO. A missing `fieldCount` means "the API did not say", and
 * reading that as zero would grey out every row in the library the day the key
 * is renamed — this repo's single most repeated bug. Only an explicit 0 counts.
 */
export function hasNoFields(template: FormLibraryTemplate): boolean {
  return template.fieldCount === 0;
}

/** The one wording for why a zero-field row is unavailable. */
export const NO_FIELDS_REASON = 'This form has no fields yet — it cannot be filled out.';
